import { useCallback, useId, useMemo, useRef } from 'react';
import c from 'classnames';

import styles from './NpsScale.module.scss';

type NpsScaleProps = {
  value: number | null;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  className?: string;
};

export const NpsScale = ({
  value,
  onChange,
  min = 1,
  max = 10,
  label,
  className,
}: NpsScaleProps) => {
  const groupId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const options = useMemo(() => {
    const nums: number[] = [];
    for (let i = min; i <= max; i += 1) nums.push(i);
    return nums;
  }, [min, max]);

  const focusByValue = useCallback(
    (nextValue: number) => {
      const idx = nextValue - min;
      refs.current[idx]?.focus();
    },
    [min],
  );

  return (
    <div
      className={c(styles.root, className)}
      role='radiogroup'
      aria-label={label}
      aria-describedby={groupId}
    >
      <span id={groupId} className={styles.srOnly}>
        {label}
      </span>
      {options.map(n => {
        const checked = value === n;
        const tabIndex = checked ? 0 : value == null && n === min ? 0 : -1;

        return (
          <button
            key={n}
            ref={el => {
              refs.current[n - min] = el;
            }}
            type='button'
            role='radio'
            aria-checked={checked}
            className={c(styles.segment, checked && styles.selected)}
            tabIndex={checked ? 0 : tabIndex}
            onClick={() => onChange(n)}
            onKeyDown={e => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                const next = value == null ? min : Math.min(max, value + 1);
                onChange(next);
                focusByValue(next);
              }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                const next = value == null ? min : Math.max(min, value - 1);
                onChange(next);
                focusByValue(next);
              }
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onChange(n);
              }
            }}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
};
